import { useOne, useTranslation } from "@refinedev/core";
import Input from "components/input";
import { Label } from "components/label";
import { IModalProps } from "components/modal";
import { RightSideModal } from "components/rightSideModal";
import { endpointService } from "components/shared/services/endpointService";
import { SwitchButton } from "components/switchButton";
import {
  IMultilingualContent,
  ISimpleEventDetail,
  ISubTicketItem,
  ProductType,
  SupportedLanguage,
} from "interfaces";
import { get } from "lodash";
import moment from "moment";
import { PRICE_TYPES, SELLING_TYPE } from "utills/constants";
import { currencySymbol } from "utills/helpers";

interface NoPhaseTicketProps extends IModalProps {
  eventDetailData: ISimpleEventDetail;
  subTicketData?: ISubTicketItem | null;
}

interface IAttendantTemplateField {
  template_id: string;
  name: IMultilingualContent;
}

export const NoPhaseTicket = ({
  open,
  onClose,
  eventDetailData,
  subTicketData,
  title,
}: NoPhaseTicketProps) => {
  const { translate } = useTranslation();
  const { data } = useOne({
    resource: endpointService.getEventEndpoint(
      "getAttendantTemplateByEventId",
      {
        eventId: eventDetailData.id ?? "",
      }
    ),
    id: "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!eventDetailData.id,
    } as any,
  });

  const templatesDataArr = (data?.data ?? []).map((template: any) => ({
    label: get(template, "attendant_template_name", ""),
    value: get(template, "id", ""),
  }));

  return (
    <RightSideModal
      title={title ?? translate("eventSetup.phaseAndTicket.ticket")}
      open={open}
      onClose={onClose}
    >
      <div className="p-4 sm:px-6">
        <div className="grid sm:grid-cols-6 gap-y-6 gap-x-4 text-sm">
          <div className="col-span-full pb-2 border-b-[1px] border-b-gray-200">
            <p className="text-sm">
              {translate("eventSetup.phaseAndTicket.modal.ticket.ticketInfo")}
            </p>
            {subTicketData?.id ? (
              <p className="text-sm mt-1">
                {translate("eventSetup.phaseAndTicket.modal.ticket.productId")}:{" "}
                {subTicketData?.id}
              </p>
            ) : (
              <></>
            )}
          </div>
          <div className="col-span-full">
            <Label
              required
              id={`category_${eventDetailData.languages[0] ?? "en"}`}
              name={translate(
                "eventSetup.phaseAndTicket.modal.ticket.category"
              )}
            />
            <div className="flex flex-col gap-y-3">
              {eventDetailData.languages.map((lang: SupportedLanguage) => (
                <div key={lang}>
                  <div className="relative flex items-center gap-x-2">
                    <img
                      src={`/images/flags/circle-${lang}.png`}
                      alt="Language Icon"
                      className="size-5"
                    />
                    {get(subTicketData?.category, lang, "")}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="col-span-full">
            <Label
              required
              id={`type_name_${eventDetailData.languages[0] ?? "en"}`}
              name={translate(
                "eventSetup.phaseAndTicket.modal.ticket.ticketNameDisplayed"
              )}
            />
            <div className="flex flex-col gap-y-3">
              {eventDetailData.languages.map((lang: SupportedLanguage) => (
                <div key={lang}>
                  <div className="relative flex items-center gap-x-2">
                    <img
                      src={`/images/flags/circle-${lang}.png`}
                      alt="Language Icon"
                      className="size-5"
                    />
                    {get(subTicketData?.type_name, lang, "")}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="col-span-full">
            <Label
              id={`short_description_${eventDetailData.languages[0] ?? "en"}`}
              name={translate(
                "eventSetup.phaseAndTicket.modal.ticket.noteBelowTicketName"
              )}
            />
            <div className="flex flex-col gap-y-4">
              {eventDetailData.languages.map((lang: SupportedLanguage) => (
                <div key={lang}>
                  <div className="relative flex items-center gap-x-2">
                    <img
                      src={`/images/flags/circle-${lang}.png`}
                      alt="Language Icon"
                      className="size-5"
                    />
                    {get(subTicketData?.short_description, lang, "") || (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="sm:col-span-3">
            <Label
              id="selling_min_qty"
              name={translate(
                "eventSetup.phaseAndTicket.modal.ticket.minSelectionQuantity"
              )}
              required
            />
            {get(subTicketData, "selling_min_qty", 0)}
          </div>
          <div className="sm:col-span-3">
            <Label
              id="selling_max_qty"
              name={translate(
                "eventSetup.phaseAndTicket.modal.ticket.maxSelectionQuantity"
              )}
              required
            />
            {get(subTicketData, "selling_max_qty", 0)}
          </div>
          <div className="sm:col-span-3">
            <Label
              id="selling_type"
              name={translate(
                "eventSetup.phaseAndTicket.modal.ticket.sellingType"
              )}
              required
            />
            {
              SELLING_TYPE.find(
                (selected: { label: string; value: string }) =>
                  selected.value === get(subTicketData, "selling_type", "")
              )?.label
            }
          </div>
          {subTicketData?.product_type === ProductType.TICKET ? ( // hidden for group tickets
            <div className="sm:col-span-3">
              <Label
                id="is_use_private_flow"
                name={translate(
                  "eventSetup.phaseAndTicket.modal.ticket.useForPrivateFlow"
                )}
              />
              <Input
                type="checkbox"
                label=""
                id="is_use_private_flow"
                checked={subTicketData?.is_use_private_flow ? true : false}
                onChange={() => {}}
                disabled
              />
            </div>
          ) : (
            <></>
          )}
          <div className="col-span-full">
            <Label
              id="total_for_sell"
              name={translate(
                "eventSetup.phaseAndTicket.modal.ticket.totalForSell"
              )}
            />
            {get(subTicketData, "total_for_sell", null) || (
              <span className="text-gray-400">N/A</span>
            )}
          </div>
          <div className="col-span-full pb-2 border-b-[1px] border-b-gray-200">
            <p className="text-sm">
              {translate("eventSetup.phaseAndTicket.modal.ticket.priceInfo")}
            </p>
          </div>
          <div className="col-span-full">
            <div className="flex flex-row flex-wrap gap-x-3">
              {PRICE_TYPES.map((type) => (
                <div
                  key={type.value}
                  className={`rounded-md ${
                    type.value === subTicketData?.price_type
                      ? "bg-[#A7B4FE] hover:bg-indigo-300 focus-visible:outline-indigo-300"
                      : "bg-white ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                  } px-6 py-3 text-sm font-semibold text-brand-500 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2`}
                >
                  {type.label}
                </div>
              ))}
            </div>
          </div>
          <div className="sm:col-span-3">
            <Label
              id="original_price"
              name={translate(
                "eventSetup.phaseAndTicket.modal.ticket.originalPrice"
              )}
              required
            />
            {get(subTicketData, "original_price", 0)}{" "}
            {currencySymbol(eventDetailData.currency)}
          </div>

          <div className="sm:col-span-3">
            <Label
              id="price"
              name={translate(
                "eventSetup.phaseAndTicket.modal.ticket.sellPrice"
              )}
              required
            />
            {get(subTicketData, "price", 0)}{" "}
            {currencySymbol(eventDetailData.currency)}
          </div>

          <div className="sm:col-span-3">
            <Label
              id="sell_from"
              name={translate(
                "eventSetup.phaseAndTicket.modal.ticket.startDate"
              )}
              required
            />
            {subTicketData?.sell_from
              ? moment(subTicketData.sell_from).format("HH:mm DD/MM/YYYY")
              : ""}
          </div>

          <div className="sm:col-span-3">
            <Label
              id="sell_to"
              name={translate("eventSetup.phaseAndTicket.modal.ticket.endDate")}
              required
            />
            {subTicketData?.sell_to
              ? moment(subTicketData.sell_to).format("HH:mm DD/MM/YYYY")
              : ""}
          </div>
          <div className="col-span-full pb-2 border-b-[1px] border-b-gray-200">
            <p className="text-sm">
              {translate(
                "eventSetup.phaseAndTicket.modal.ticket.participantFormInfo"
              )}
            </p>
          </div>
          <div className="col-span-full">
            <SwitchButton
              label={translate(
                "eventSetup.phaseAndTicket.modal.ticket.isPackage"
              )}
              checked={subTicketData?.is_package ?? false}
              onChange={() => {}}
              disabled
            />
          </div>
          <div className="col-span-full">
            <div className="divide-y divide-gray-300">
              {(subTicketData?.attendant_template ?? []).map(
                (field: IAttendantTemplateField, index: number) => (
                  <div key={index} className="py-4">
                    <div>
                      <Label
                        id={`attendant_template.${index}.template_id`}
                        name={translate(
                          "eventSetup.phaseAndTicket.modal.ticket.selectParticipantForm"
                        )}
                        required
                      />
                      {
                        templatesDataArr.find(
                          (selected: { label: string; value: string }) =>
                            selected.value === get(field, "template_id", "")
                        )?.label
                      }
                    </div>
                    {subTicketData?.is_package ? (
                      <div className="mt-4">
                        <Label
                          id={`attendant_template.${index}.name`}
                          name={translate(
                            "eventSetup.phaseAndTicket.modal.ticket.formNameDisplayed"
                          )}
                        />
                        <div className="flex flex-col gap-y-4">
                          {eventDetailData.languages.map((lang) => (
                            <div key={lang}>
                              <div className="relative flex items-center gap-x-2">
                                <img
                                  src={`/images/flags/circle-${lang}.png`}
                                  alt="Language Icon"
                                  className="size-5"
                                />
                                {get(field.name, lang, "")}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <></>
                    )}
                  </div>
                )
              )}
            </div>
          </div>
          <div className="col-span-full pb-2 border-b-[1px] border-b-gray-200">
            <p className="text-sm">
              {translate(
                "eventSetup.phaseAndTicket.modal.ticket.advanceSettings"
              )}
            </p>
          </div>
          <div className="col-span-full flex flex-col items-start gap-y-4">
            <SwitchButton
              label={translate("eventSetup.phaseAndTicket.modal.ticket.show")}
              hint={translate(
                "eventSetup.phaseAndTicket.modal.ticket.showHint"
              )}
              checked={!subTicketData?.hide_in_ui}
              onChange={() => {}}
              disabled
            />
            <SwitchButton
              label={translate(
                "eventSetup.phaseAndTicket.modal.ticket.acceptEditParticipantInfo"
              )}
              hint={translate(
                "eventSetup.phaseAndTicket.modal.ticket.acceptEditParticipantInfoHint"
              )}
              checked={subTicketData?.is_accept_edit_participant_info ?? false}
              onChange={() => {}}
              disabled
            />
            <SwitchButton
              label={translate(
                "eventSetup.phaseAndTicket.modal.ticket.acceptUpgradeTicket"
              )}
              hint={translate(
                "eventSetup.phaseAndTicket.modal.ticket.acceptUpgradeTicketHint"
              )}
              checked={subTicketData?.is_accept_upgrade_ticket ?? false}
              onChange={() => {}}
              disabled
            />
            <SwitchButton
              label={translate(
                "eventSetup.phaseAndTicket.modal.ticket.acceptTransferTicket"
              )}
              hint={translate(
                "eventSetup.phaseAndTicket.modal.ticket.acceptTransferTicketHint"
              )}
              checked={subTicketData?.is_accept_transfer_ticket ?? false}
              onChange={() => {}}
              disabled
            />
            <SwitchButton
              label={translate(
                "eventSetup.phaseAndTicket.modal.ticket.isSoldOut"
              )}
              hint={translate(
                "eventSetup.phaseAndTicket.modal.ticket.isSoldOutHint"
              )}
              checked={subTicketData?.is_soldout ?? false}
              onChange={() => {}}
              disabled
            />
          </div>
        </div>
      </div>
    </RightSideModal>
  );
};
